import { PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse } from 'apisauce';
import moment from 'moment';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import { debounceTime, filter, mergeMap } from 'rxjs/operators';

import {
  CryptocurrenciesEnum,
  CurrencyEnum,
  HttpStatusCodeEnum,
  PaymentMethodEnum,
  PaymentProcessEnum,
  PaymentProviderEnum,
  PredefinedMilisecondsTypeEnum,
} from '../../enums';
import { GetPaymentsPayload, Payment, PaymentsFilter, WithdrawalRequest } from '../../models/crm/types';
import { CardTransferStatusSuccessParam, GetCryptoRateParams } from '../../models/payment/types';
import GlobalService from '../../services/Global.service';
import { isPendingAccountStatus } from '../../util/account-helpers';
import { isAllStartupPaymentCallsDone } from '../../util/error-handling/StatusByCallHelpers';
import { ErrorPayloadFull, getAccountsAvailableCash, getAccountsAvailableCashSuccess, getOwnerTradingAccountsFailed, refreshPayments, refreshPaymentsDone } from '../common-actions';
import { accountsCompleted, setUseDemoAccount } from '../crm';
import { getCurrentDetailedAccount } from '../crm/helpers';
import { processErrorInResponse, processResponse } from '../helpers';
import { hasValidCookie, isUserVerifiedStatus } from '../selectors';
import { RootState } from '..';

import { ALL_TRANSACTIONS_FILTER, TRANSACTIONS_FILTER } from './constants';
import { getBankStatementRequestBody, getCryptoLinkedAccountRequestBody } from './helpers';
import {
  confirmInitialPaymentsLoaded,
  getCardTransferStatus,
  getCardTransferStatusFail,
  getCardTransferStatusSuccess,
  getCryptoRate,
  getCryptoRateFail,
  getCryptoRateSuccess,
  getIndividualLinkedAccounts,
  getIndividualLinkedAccountsFailed,
  getIndividualLinkedAccountsSuccess,
  getOwnerTradingAccounts,
  getOwnerTradingAccountsSuccess,
  getPayments,
  getPaymentsAllCompleted,
  getPaymentsAllFailed,
  getPaymentsCompleted,
  getPaymentsFailed,
  getSolanaPayments,
  getSolanaPaymentsCompleted,
  getSolanaPaymentsFailed,
  registerCryptoLinkedAccount,
  registerCryptoLinkedAccountFailed,
  registerCryptoLinkedAccountSuccess,
  registerCryptoWithdrawalRequest,
  registerCryptoWithdrawalRequestFail,
  registerCryptoWithdrawalRequestSuccess,
  registerLinkedAccount,
  registerLinkedAccountFailed,
  registerLinkedAccountSuccess,
  registerWithdrawalRequest,
  registerWithdrawalRequestFail,
  registerWithdrawalRequestSuccess,
} from './index';
import { GetSolanaPaymentsPayload, PaymentCall } from './types';

/**
 * Startup loading of basic payments data - transactions, bank accounts, cash. It waits for the
 * accounts call, which is what settles which account the data is loaded for; an account which is
 * still being created has nothing to load yet.
 */
const initialLoadEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(accountsCompleted.type, setUseDemoAccount.type),
  filter(() => {
    const { account_status } = getCurrentDetailedAccount(state$.value) ?? {};
    return account_status !== undefined && !isPendingAccountStatus(account_status);
  }),
  filter(() => !(state$.value.payment.payments?.data?.length > 0)),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    observer.next(getOwnerTradingAccounts()); // 1
    observer.next(getIndividualLinkedAccounts()); // linked accounts / aka bank accounts
    observer.next(getAccountsAvailableCash({})); // cash
    observer.next(getPayments(ALL_TRANSACTIONS_FILTER)); // all transactions + pending deposits sum (see `processAllTransactionsEpic`)
  })),
);

const checkInitialLoadEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(
    getOwnerTradingAccountsSuccess.type,
    getIndividualLinkedAccountsSuccess.type,
    getAccountsAvailableCashSuccess.type,
    getPaymentsAllCompleted.type,
  ),
  filter(() => !state$.value.payment.isInitialPaymentsDataLoaded),
  filter(() => !state$.value.app.connected),
  filter(() => (
    isAllStartupPaymentCallsDone(state$.value.payment.statusByCall)
    || !isUserVerifiedStatus(state$.value.crm.individualExtendedInfo)
  )),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    observer.next(confirmInitialPaymentsLoaded());
  })),
);


/**
 * Reloading of basic payments data - transactions, bank accounts, cash
 */
const refreshEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(refreshPayments.type),
  filter(action => !!action.payload || !!state$.value.payment.callsForRefresh),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callsForRefresh = action.payload ?? state$.value.payment.callsForRefresh;
    if (callsForRefresh[PaymentCall.getIndividualLinkedAccounts]) {
      observer.next(getIndividualLinkedAccounts());
    }
    if (callsForRefresh[PaymentCall.getAvailableCash]) {
      observer.next(getAccountsAvailableCash({}));
    }
    if (callsForRefresh[PaymentCall.getPayments]) {
      observer.next(getPayments(TRANSACTIONS_FILTER));
    }
    if (callsForRefresh[PaymentCall.getPaymentsAll]) {
      observer.next(getPayments(ALL_TRANSACTIONS_FILTER));
    }

    observer.next(refreshPaymentsDone());
  })),
);


/**
 * Get bank accounts
 */
const getLinkedAccountsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getIndividualLinkedAccounts.type),
  mergeMap((action: any) => new Observable((observer: Observer<any>) => {
    const callName = 'payment/linkedAccountsByIndividual';

    const { individualOrCompanyEmployeeId: individualId } = state$.value.crm;

    if (!individualId) {
      processErrorInResponse(
        callName,
        new Error('[Payment] Individual id not found'),
        action,
        observer,
        getIndividualLinkedAccountsFailed,
      );

      return;
    }

    const id = typeof individualId !== 'number' ? parseInt(individualId, 10) : individualId;

    GlobalService
      .crmService
      .getLinkedAccountsByIndividualId(id)
      .then(((response: any) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getIndividualLinkedAccountsSuccess,
          getIndividualLinkedAccountsFailed,
          false,
          null,
          null,
          null,
          null,
          { 404: [] },
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, getIndividualLinkedAccountsFailed));
  })),
);



/**
 * Get transactions
 */
const getPaymentsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getPayments.type),
  filter(action => (
    state$.value.payment.payments.callsInQueue.length === 0
    || action.payload.isQueued
  )),
  mergeMap((
    action: PayloadAction<GetPaymentsPayload>,
  ) => new Observable((observer: Observer<any>) => {
    if (state$.value.crm.individualOrCompanyEmployeeId === null) {
      console.error('[payment/epics] getPaymentsEpic - individual id not found');
      return;
    }
    const { paymentProcess, pageNumber, dateOffset, pageSize, paymentStatus } = action.payload;
    const { individualOrCompanyEmployeeId: individualId } = state$.value.crm;
    const { payments, paymentsAll } = state$.value.payment;
    const isAllTransactionsCall = !!payments.isAllTransactionsCall;
    const transactions = isAllTransactionsCall ? paymentsAll : payments;

    // TODO: const dateFrom = getDateTime()
    const dateFrom = moment().subtract(dateOffset, 'd').format('YYYY-MM-DD');
    const dateTo = moment().format('YYYY-MM-DD');
    const paymentsFilter: PaymentsFilter = {
      account_owner_id: individualId,
      is_account_owner_company: false,
      date_from: dateFrom,
      date_to: dateTo,
      payment_process: paymentProcess,
      page_number: pageNumber,
      page_size: pageSize ?? transactions.pageSize,
      order_column: transactions.orderColumn,
      order_ascending: transactions.orderAscending,
      payment_status: paymentStatus,
    };

    const callName = 'payment/getPayments';
    let successAction: any;
    let failAction: any;
    if (isAllTransactionsCall) {
      successAction = getPaymentsAllCompleted;
      failAction = getPaymentsAllFailed;
    } else {
      successAction = getPaymentsCompleted;
      failAction = getPaymentsFailed;
    }

    GlobalService
      .crmService
      .getPayments(paymentsFilter)
      .then((rawResponse: ApiResponse<Payment[]>) => {
        /**
         * CRM returns status code 404(Not found) if there aren't records by filter criteria
         */
        let response = { ...rawResponse };
        if (response.status === HttpStatusCodeEnum.NotFound) {
          response.status = HttpStatusCodeEnum.Ok;
          response.data = [];
        }

        processResponse(
          callName,
          {},
          response,
          observer,
          successAction,
          failAction,
          false,
          null,
          null,
          null,
          null,
          { 404: [] },
        );
      })
      .catch(error => {
        processErrorInResponse(callName, error, action, observer, failAction);
      });
  })),
);

const getPaymentsQueuedEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getPaymentsCompleted.type, getPaymentsFailed.type),
  filter(() => state$.value.payment.payments.callsInQueue.length > 0),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    observer.next(getPayments(state$.value.payment.payments.callsInQueue[0]));
  })),
);



/**
 * Register bank account
 */
const registerLinkedAccountEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(registerLinkedAccount.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'payment/registerLinkedAccount';
    const { individualOrCompanyEmployeeId: individualId } = state$.value.crm;

    if (!individualId) {
      processErrorInResponse(callName, new Error('[Payment] Individual id not found'), action, observer, registerLinkedAccountFailed);
      return;
    }

    const requestBody = getBankStatementRequestBody(individualId, action.payload);

    GlobalService
      .crmService
      .registerLinkedAccount(requestBody)
      .then((response: any) => {
        processResponse(callName, {}, response, observer, registerLinkedAccountSuccess, registerLinkedAccountFailed);
      })
      .catch((error: any) => {
        processErrorInResponse(callName, error, action, observer, registerLinkedAccountFailed);
      });
  })),
);

/**
 * Register crypto wallet as linked account
 */
const registerCryptoLinkedAccountEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(registerCryptoLinkedAccount.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'payment/registerCryptoLinkedAccount';
    const { individualOrCompanyEmployeeId: individualId } = state$.value.crm;

    if (!individualId) {
      processErrorInResponse(callName, new Error('[Payment] Individual id not found'), action, observer, registerCryptoLinkedAccountFailed);
      return;
    }

    const requestBody = getCryptoLinkedAccountRequestBody(individualId, action.payload);

    GlobalService
      .crmService
      .registerCryptoLinkedAccount(requestBody)
      .then((response: any) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          registerCryptoLinkedAccountSuccess,
          registerCryptoLinkedAccountFailed,
        );
      })
      .catch((error: any) => {
        processErrorInResponse(callName, error, action, observer, registerCryptoLinkedAccountFailed);
      });
  })),
);


const getOwnerTradingAccountsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getOwnerTradingAccounts.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'payment/getOwnerTradingAccounts';
    if (!state$.value.crm.individualExtendedInfo?.id) {
      const errorStr = '[getOwnerTradingAccountsEpic] Individual id not available';
      observer.next(getOwnerTradingAccountsFailed({
        callName: 'getOwnerTradingAccounts',
        errorData: {},
        errorStr,
      } as ErrorPayloadFull));
      return;
    }

    GlobalService
      .crmService
      .getOwnerTradingAccounts(state$.value.crm.individualExtendedInfo?.id)
      .then(((response: any) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getOwnerTradingAccountsSuccess,
          getOwnerTradingAccountsFailed,
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, getOwnerTradingAccountsFailed));
  })),
);

/**
 * Initiate withdrawal
 */
const registerWithdrawalRequestEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(registerWithdrawalRequest.type),
  mergeMap((action: any) => new Observable((observer: Observer<any>) => {
    const callName = 'payment/registerWithdrawalRequest';
    const data = action.payload as WithdrawalRequest;

    data.to_internal_account_id = state$.value.payment.tradingAccounts.find(acc => acc.is_cash_account)?.id;
    data.currency_code = action.payload.currency_code ?? CurrencyEnum.USD;
    data.payment_process = PaymentProcessEnum.Withdrawal;
    data.payment_method = PaymentMethodEnum.BankTransfer;
    data.payment_provider = PaymentProviderEnum.UnknownBank;

    GlobalService
      .crmService
      .registerWithdrawalRequest(data)
      .then(((response: any) => {
        processResponse(
          callName,
          data,
          response,
          observer,
          registerWithdrawalRequestSuccess,
          registerWithdrawalRequestFail,
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, registerWithdrawalRequestFail));
  })),
);

/**
 * Initiate crypto withdrawal
 */
const registerCryptoWithdrawalRequestEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(registerCryptoWithdrawalRequest.type),
  mergeMap((action: any) => new Observable((observer: Observer<any>) => {
    const callName = 'payment/registerCryptoWithdrawalRequest';
    const data = action.payload as WithdrawalRequest;

    data.to_internal_account_id = state$.value.payment.tradingAccounts.find(acc => acc.is_cash_account)?.id;
    data.currency_code = action.payload.currency_code ?? CurrencyEnum.USD;
    data.payment_process = PaymentProcessEnum.Withdrawal;
    data.payment_method = PaymentMethodEnum.Solana;
    data.payment_provider = PaymentProviderEnum.Solana;
    data.crypto_symbol = CryptocurrenciesEnum.USDT;

    GlobalService
      .crmService
      .registerWithdrawalRequest(data)
      .then(((response: any) => {
        processResponse(
          callName,
          data,
          response,
          observer,
          registerCryptoWithdrawalRequestSuccess,
          registerCryptoWithdrawalRequestFail,
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, registerCryptoWithdrawalRequestFail));
  })),
);


/**
 * Check card deposit status
 */
const getCardTransferStatusEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getCardTransferStatus.type),
  debounceTime(PredefinedMilisecondsTypeEnum.oneSecond),
  filter(() => hasValidCookie(state$.value)),
  mergeMap((action: PayloadAction<CardTransferStatusSuccessParam>) => new Observable((observer: Observer<any>) => {
    const paymentState = state$.value.payment;
    const { webLastCardTransfer, lastCardTransfer } = paymentState;

    const lastPaymentRef = action.payload.paymentReference ?? webLastCardTransfer.payment_reference;

    if (lastPaymentRef) {
      GlobalService
        .paymentService
        .getCardTransferStatus(lastPaymentRef, action.payload.paymentProvider ?? PaymentProviderEnum.Stripe)
        .then((response: any) => {
          if (response.ok) {
            observer.next(getCardTransferStatusSuccess(response.data));
          } else {
            observer.next(getCardTransferStatusFail(response.data));
          }
        });
    } else {
      console.warn(
        `[payment/epics] Skipping 'getCardTransferStatus' call to Payment API - no payment reference~ '${lastPaymentRef}'`,
        { lastPaymentRef, webLastCardTransfer, lastCardTransfer },
      );
    }
  })),
);

const getSolanaPaymentsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getSolanaPayments.type),
  mergeMap((action: PayloadAction<GetSolanaPaymentsPayload>) => new Observable((observer: Observer<any>) => {
    const callName = 'payment/getSolanaPayments';
    const paymentFilter: PaymentsFilter = {
      page_number: 1,
      page_size: action.payload.pageSize ?? 2,
      order_column: 'create_date',
      order_ascending: false,
      payment_status: action.payload.statuses,
    };

    GlobalService
      .crmService
      .getPayments(paymentFilter)
      .then((response: ApiResponse<Payment[]>) => {
        if (response.status === HttpStatusCodeEnum.Ok) {
          observer.next(getSolanaPaymentsCompleted(response.data ?? []));
        } else if (response.status === HttpStatusCodeEnum.NotFound) {
          observer.next(getSolanaPaymentsCompleted([]));
        } else {
          console.error(`[${callName}] Unexpected status: ${response.status}`);
          observer.next(getSolanaPaymentsFailed());
        }
      })
      .catch(error => {
        processErrorInResponse(callName, error, { payload: filter }, observer, getSolanaPaymentsFailed);
      });
  })),
);
/**
 * Get crypto rate
 */
const getCryptoRateEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getCryptoRate.type),
  filter(() => hasValidCookie(state$.value)),
  mergeMap((action: PayloadAction<GetCryptoRateParams | undefined>) => new Observable((observer: Observer<any>) => {
    GlobalService
      .paymentService
      .getCryptoRate(action.payload)
      .then((response: any) => {
        if (response.ok) {
          observer.next(getCryptoRateSuccess(response.data));
        } else {
          observer.next(getCryptoRateFail(response.data));
        }
      });
  })),
);

export default combineEpics(
  initialLoadEpic,
  checkInitialLoadEpic,
  refreshEpic,
  getPaymentsEpic,
  getPaymentsQueuedEpic,
  getLinkedAccountsEpic,
  registerLinkedAccountEpic,
  registerCryptoLinkedAccountEpic,
  getOwnerTradingAccountsEpic,
  registerWithdrawalRequestEpic,
  registerCryptoWithdrawalRequestEpic,
  getCardTransferStatusEpic,
  getSolanaPaymentsEpic,
  getCryptoRateEpic,
);
